import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { logoutCliente } from "../../services/authCliente";
import { getPerfilCliente, getDireccionesCliente, getPedidosCliente, updatePerfilCliente, changePasswordCliente,
createDireccionCliente, updateDireccionCliente, deleteDireccionCliente } from "../../services/cliente/perfilcliente";
import OrdersModal from "./OrdersModal";
import { traducirEstadoPedido } from "../../utils/estadoPedido";
import "./AccountPage.css";
import phoneCountries from "../../catalogs/phoneCountries";
import OrderDocsModal from "./OrderDocsModal";

const getErrMsg = (error, fallback = "Something went wrong") => {
  const raw =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message;

  if (!raw) return fallback;
  if (Array.isArray(raw)) return raw.join(", ");
  if (typeof raw === "object") return fallback;
  return String(raw);
};

const norm = (v) => String(v ?? "").trim();
const normBool = (v) => !!v;

const shallowEqualByKeys = (a, b, keys) => {
  for (const k of keys) {
    const av = a?.[k];
    const bv = b?.[k];

    if (typeof av === "boolean" || typeof bv === "boolean") {
      if (normBool(av) !== normBool(bv)) return false;
      continue;
    }

    if (norm(av) !== norm(bv)) return false;
  }
  return true;
};
const onlyDigits = (v) => String(v ?? "").replace(/\D+/g, "");
function ToastStack({ toasts, onClose }) {
  if (!toasts?.length) return null;

  return (
    <div className="adc-toastStack" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`adc-toast adc-toast--${t.type || "info"}`}>
          <div className="adc-toast__body">
            <div className="adc-toast__title">{t.title}</div>
            {t.message ? <div className="adc-toast__msg">{t.message}</div> : null}
          </div>

          <button
            className="adc-toast__close"
            onClick={() => onClose(t.id)}
            aria-label="Close notification"
            type="button"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

export const formatMoney = (value, locale = "es-GT") => {
  const n = Number(String(value ?? 0).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "$0.00";

  const formatted = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  return formatted;
};

export default function AccountPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [docsOrderId, setDocsOrderId] = useState(null); 
  const [profile, setProfile] = useState({
    nombre: "",
    apellido: "",
    usuario: "",
    correo: "",
    codigo_telefono: "+502",
    telefono: "",
    // Solo se usan cuando la cuenta es comercial.
    tipo_cliente: "individual",
    empresa: "",
    razon_social: "",
    vat_tax_id: "",
    correo_facturacion: "",
    currentPassword: "",
    newPassword: "",
  });
  const [loadingPerfil, setLoadingPerfil] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);
  const profileBaselineRef = useRef(null);
  const addressBaselineRef = useRef(null);
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef(new Map());
  const [showDocs, setShowDocs] = useState(false);

  const pushToast = (type, title, message, opts = {}) => {
    const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const duration = Number.isFinite(opts.duration) ? opts.duration : 4200;

    const toast = { id, type, title, message };
    setToasts((prev) => [toast, ...prev].slice(0, 4));

    const timer = window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      toastTimers.current.delete(id);
    }, duration);

    toastTimers.current.set(id, timer);
    return id;
  };

  const closeToast = (id) => {
    const timer = toastTimers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      toastTimers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    return () => {
      toastTimers.current.forEach((timer) => window.clearTimeout(timer));
      toastTimers.current.clear();
    };
  }, []);

  /* Une las partes que existan. Sin esto, una direccion sin estado ni
     codigo postal (los dos son opcionales) imprime "Guatemala,  · " con la
     coma y el punto colgando. */
  const unir = (partes, sep = ", ") => partes.map(norm).filter(Boolean).join(sep);

  const emptyAddress = {
    cliente_direccion_id: null,
    nombre_direccion: "",
    nombre_destinatario: "",
    codigo_telefono: "",
    telefono: "",
    empresa: "",
    direccion_linea_1: "",
    direccion_linea_2: "",
    ciudad: "",
    estado_provincia: "",
    codigo_postal: "",
    pais: "",
    direccion_principal: false,
    direccion_facturacion: false,
  };

  /* El catalogo trae cinco paises. Sirve para sugerir cuantos digitos lleva
     el numero cuando el codigo es uno de esos, pero ya no manda: el codigo
     se escribe. Antes era un desplegable con esos cinco, y un cliente de
     cualquier otro pais no podia guardar su perfil. */
  const phoneMeta = useMemo(() => {
    const code = norm(profile.codigo_telefono);
    return phoneCountries.find((c) => c.code === code) || null;
  }, [profile.codigo_telefono]);

  /* El "+" se pone solo y se descarta todo lo demas. Ningun codigo de pais
     empieza con cero: quien escribe "00502" esta poniendo el prefijo
     internacional de su linea, que no es parte del codigo. */
  const limpiarCodigoPais = (valor) => {
    const texto = String(valor ?? "");
    if (texto.trim() === "") return "";
    return `+${texto.replace(/\D/g, "").replace(/^0+/, "").slice(0, 6)}`;
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    if (name === "telefono") {
      setProfile((p) => ({ ...p, telefono: onlyDigits(value) }));
      return;
    }
    if (name === "codigo_telefono") {
      setProfile((p) => ({ ...p, codigo_telefono: limpiarCodigoPais(value) }));
      return;
    }

    setProfile((p) => ({ ...p, [name]: value }));
  };

  const esCuentaComercial = profile.tipo_cliente === "comercial";

  /* Cambiar la contrasena exige la anterior. Se avisa mientras escribe y no
     al apretar Guardar: llenar la nueva, mandarla y recien ahi enterarse de
     que faltaba la actual obliga a rehacer los dos campos. */
  const faltaPasswordActual =
    norm(profile.newPassword) !== "" && norm(profile.currentPassword) === "";

  const faltaPasswordNueva =
    norm(profile.currentPassword) !== "" && norm(profile.newPassword) === "";

  const isProfileDirty = useMemo(() => {
    const base = profileBaselineRef.current;
    if (!base) return false;

    const baseCore = {
      nombre: base.nombre,
      apellido: base.apellido,
      correo: base.correo,
      codigo_telefono: base.codigo_telefono,
      telefono: base.telefono,
      empresa: base.empresa,
      razon_social: base.razon_social,
      vat_tax_id: base.vat_tax_id,
      correo_facturacion: base.correo_facturacion,
    };

    const currentCore = {
      nombre: profile.nombre,
      apellido: profile.apellido,
      correo: profile.correo,
      codigo_telefono: profile.codigo_telefono,
      telefono: profile.telefono,
      empresa: profile.empresa,
      razon_social: profile.razon_social,
      vat_tax_id: profile.vat_tax_id,
      correo_facturacion: profile.correo_facturacion,
    };

    const coreChanged = !shallowEqualByKeys(baseCore, currentCore, [
      "nombre",
      "apellido",
      "correo",
      "codigo_telefono",
      "telefono",
      "empresa",
      "razon_social",
      "vat_tax_id",
      "correo_facturacion",
    ]);

    const wantsPassword =
      norm(profile.currentPassword) !== "" || norm(profile.newPassword) !== "";

    return coreChanged || wantsPassword;
    /* Depende del objeto entero y no de campo por campo. Con la lista a
       mano, agregar un campo al formulario y olvidarse de sumarlo aca deja
       el boton de guardar apagado al editarlo, sin ningun error a la vista:
       fue lo que paso al sumar los datos de empresa. `setProfile` siempre
       crea un objeto nuevo, asi que esto recalcula con cualquier cambio. */
  }, [profile]);

  const validatePhone = () => {
    const code = norm(profile.codigo_telefono);
    const phone = onlyDigits(profile.telefono);
    const hasAny = !!code || !!phone;
    if (!hasAny || (!code && !phone)) return { ok: true, clean: { code: "", phone: "" } };
    if (code && !phone) {
      return {
        ok: false,
        message: "Enter your phone number or clear the country code.",
      };
    }
    if (!code && phone) {
      return {
        ok: false,
        message: "Select a country code for your phone number.",
      };
    }

    /* Antes esto exigia que el codigo estuviera en el catalogo de cinco
       paises, asi que un cliente de cualquier otro no podia guardar su
       perfil. Ahora solo se comprueba la forma; el largo del numero se
       revisa contra el catalogo unicamente cuando el pais figura ahi. */
    if (!/^\+\d{1,6}$/.test(code)) {
      return { ok: false, message: "Country code is not valid." };
    }

    const meta = phoneCountries.find((c) => c.code === code);
    if (!meta) {
      return phone.length >= 6 && phone.length <= 20
        ? { ok: true, clean: { code, phone } }
        : { ok: false, message: "Phone number must be 6 to 20 digits." };
    }

    const len = phone.length;
    if (len < meta.minLength || len > meta.maxLength) {
      return {
        ok: false,
        message: `Phone number for ${meta.country} must be ${meta.minLength}${
          meta.minLength === meta.maxLength ? "" : `–${meta.maxLength}`
        } digits.`,
      };
    }

    return { ok: true, clean: { code, phone } };
  };

  const saveProfile = async () => {
    if (!isProfileDirty) {
      pushToast("info", "No changes", "There’s nothing new to save.");
      return;
    }

    setSavingProfile(true);
    try {
      const base = profileBaselineRef.current || {
        nombre: "",
        apellido: "",
        correo: "",
        codigo_telefono: "",
        telefono: "",
        empresa: "",
        razon_social: "",
        vat_tax_id: "",
        correo_facturacion: "",
      };

      const CLAVES = [
        "nombre",
        "apellido",
        "correo",
        "codigo_telefono",
        "telefono",
        "empresa",
        "razon_social",
        "vat_tax_id",
        "correo_facturacion",
      ];

      const coreChanged = !shallowEqualByKeys(
        {
          nombre: base.nombre,
          apellido: base.apellido,
          correo: base.correo,
          codigo_telefono: base.codigo_telefono,
          telefono: base.telefono,
          empresa: base.empresa,
          razon_social: base.razon_social,
          vat_tax_id: base.vat_tax_id,
          correo_facturacion: base.correo_facturacion,
        },
        {
          nombre: profile.nombre,
          apellido: profile.apellido,
          correo: profile.correo,
          codigo_telefono: profile.codigo_telefono,
          telefono: profile.telefono,
          empresa: profile.empresa,
          razon_social: profile.razon_social,
          vat_tax_id: profile.vat_tax_id,
          correo_facturacion: profile.correo_facturacion,
        },
        CLAVES
      );

      if (coreChanged) {
        const phoneCheck = validatePhone();
        if (!phoneCheck.ok) {
          pushToast("warn", "Invalid phone", phoneCheck.message || "Check your phone number.");
          return;
        }

        if (esCuentaComercial && !norm(profile.razon_social)) {
          pushToast(
            "warn",
            "Legal company name required",
            "A company account needs the name that goes on its invoices."
          );
          return;
        }

        await updatePerfilCliente({
          nombre: profile.nombre,
          apellido: profile.apellido,
          correo: profile.correo,
          codigo_telefono: phoneCheck.clean.code || null,
          telefono: phoneCheck.clean.phone || null,
          // El backend los descarta si la cuenta no es comercial.
          empresa: profile.empresa,
          razon_social: profile.razon_social,
          vat_tax_id: profile.vat_tax_id,
          correo_facturacion: profile.correo_facturacion,
        });

        profileBaselineRef.current = {
          nombre: norm(profile.nombre),
          apellido: norm(profile.apellido),
          correo: norm(profile.correo),
          codigo_telefono: norm(profile.codigo_telefono),
          telefono: norm(profile.telefono),
          empresa: norm(profile.empresa),
          razon_social: norm(profile.razon_social),
          vat_tax_id: norm(profile.vat_tax_id),
          correo_facturacion: norm(profile.correo_facturacion),
        };
      }

      const wantsPassword =
        norm(profile.currentPassword) !== "" || norm(profile.newPassword) !== "";

      if (wantsPassword) {
        if (!norm(profile.currentPassword) || !norm(profile.newPassword)) {
          pushToast(
            "warn",
            "Missing password fields",
            "To change your password, enter both your current password and a new password."
          );
          return;
        }

        await changePasswordCliente({
          currentPassword: profile.currentPassword,
          newPassword: profile.newPassword,
        });

        setProfile((p) => ({ ...p, currentPassword: "", newPassword: "" }));
      }

      pushToast("success", "Changes saved", "Your profile was updated successfully.");
    } catch (error) {
      console.error(error?.response?.data || error);
      pushToast("error", "Save failed", getErrMsg(error, "We couldn’t save your profile."));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;

    // El codigo de pais se limpia igual que el del perfil.
    const limpio =
      type === "checkbox"
        ? checked
        : name === "codigo_telefono"
        ? limpiarCodigoPais(value)
        : value;

    setEditingAddress((a) => (a ? { ...a, [name]: limpio } : a));
  };

  const addNewAddress = () => {
    /* La empresa de la cuenta ya viene puesta, pero se puede cambiar: el
       pedido puede ir a otro taller, a un proveedor o a un freight
       forwarder, y ahi el nombre que va en la etiqueta es otro. */
    const next = {
      ...emptyAddress,
      empresa: esCuentaComercial
        ? norm(profile.empresa) || norm(profile.razon_social)
        : "",
    };
    addressBaselineRef.current = { ...next };
    setEditingAddress(next);
  };

  const editAddress = (addr) => {
    const next = { ...addr };
    addressBaselineRef.current = { ...next };
    setEditingAddress(next);
  };

  const isAddressDirty = useMemo(() => {
    if (!editingAddress) return false;
    const base = addressBaselineRef.current;
    if (!base) return true;

    const keys = [
      "nombre_direccion",
      "nombre_destinatario",
      "codigo_telefono",
      "telefono",
      "empresa",
      "direccion_linea_1",
      "direccion_linea_2",
      "ciudad",
      "estado_provincia",
      "codigo_postal",
      "pais",
      "direccion_principal",
      "direccion_facturacion",
    ];

    return !shallowEqualByKeys(base, editingAddress, keys);
  }, [editingAddress]);


      const formatDateTime = (value) => {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    };

  const isAddressValid = useMemo(() => {
    if (!editingAddress) return false;
    const required = [
      ["nombre_direccion", "Address name"],
      ["nombre_destinatario", "Recipient's full name"],
      ["direccion_linea_1", "Address line 1"],
      ["ciudad", "City"],
      ["estado_provincia", "State / Province"],
      ["codigo_postal", "Postal code"],
      ["pais", "Country"],
      ["codigo_telefono", "Phone country code"],
      ["telefono", "Phone number"],
    ];

    for (const [k] of required) {
      if (!norm(editingAddress[k])) return false;
    }
    return true;
  }, [editingAddress]);

  const saveAddress = async () => {
    if (!editingAddress) return;

    if (!isAddressDirty) {
      pushToast("info", "No changes", "There’s nothing new to save.");
      return;
    }

    if (!isAddressValid) {
      pushToast("warn", "Missing fields", "Please fill in all required address fields.");
      return;
    }

    setSavingAddress(true);
    try {
      const payload = { ...editingAddress };

      if (editingAddress.cliente_direccion_id) {
        await updateDireccionCliente(editingAddress.cliente_direccion_id, payload);
      } else {
        await createDireccionCliente(payload);
      }

      const updated = await getDireccionesCliente();
      setAddresses(Array.isArray(updated) ? updated : []);

      setEditingAddress(null);
      addressBaselineRef.current = null;

      pushToast("success", "Address saved", "Your address has been saved successfully.");
    } catch (error) {
      console.error(error?.response?.data || error);
      pushToast("error", "Save failed", getErrMsg(error, "We couldn’t save your address."));
    } finally {
      setSavingAddress(false);
    }
  };

  const deleteAddress = async (id) => {
    try {
      await deleteDireccionCliente(id);
      const updated = await getDireccionesCliente();
      setAddresses(Array.isArray(updated) ? updated : []);

      pushToast("success", "Address deleted", "Your address has been deleted.");
    } catch (error) {
      console.error(error?.response?.data || error);
      pushToast("error", "Delete failed", getErrMsg(error, "We couldn’t delete your address."));
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoadingPerfil(true);
      setLoadingOrders(true);

      const results = await Promise.allSettled([
        getPerfilCliente(),
        getDireccionesCliente(),
        getPedidosCliente(),
      ]);

      const [perfilRes, direccionesRes, pedidosRes] = results;

      if (perfilRes.status === "fulfilled") {
        const perfil = perfilRes.value;

        const loaded = {
          nombre: perfil?.nombre || "",
          apellido: perfil?.apellido || "",
          usuario: perfil?.usuario || "",
          correo: perfil?.correo || "",
          /* Vacio, no un espacio. Con " " el campo arrancaba con un caracter
             invisible que no es un codigo de pais y que nadie ve para
             borrar. */
          codigo_telefono: perfil?.codigo_telefono || "",
          telefono: onlyDigits(perfil?.telefono || ""),
          tipo_cliente: perfil?.tipo_cliente || "individual",
          empresa: perfil?.empresa || "",
          razon_social: perfil?.razon_social || "",
          vat_tax_id: perfil?.vat_tax_id || "",
          correo_facturacion: perfil?.correo_facturacion || "",
        };

        setProfile((prev) => ({
          ...prev,
          ...loaded,
        }));

        profileBaselineRef.current = {
          nombre: norm(loaded.nombre),
          apellido: norm(loaded.apellido),
          correo: norm(loaded.correo),
          codigo_telefono: norm(loaded.codigo_telefono),
          telefono: norm(loaded.telefono),
          empresa: norm(loaded.empresa),
          razon_social: norm(loaded.razon_social),
          vat_tax_id: norm(loaded.vat_tax_id),
          correo_facturacion: norm(loaded.correo_facturacion),
        };
      } else {
        console.error("Profile error:", perfilRes.reason);
        pushToast(
          "error",
          "Profile load failed",
          getErrMsg(perfilRes.reason, "We couldn’t load your profile details.")
        );
      }

      if (direccionesRes.status === "fulfilled") {
        const direcciones = direccionesRes.value;
        setAddresses(Array.isArray(direcciones) ? direcciones : []);
      } else {
        console.error("Addresses error:", direccionesRes.reason);
        setAddresses([]);
        pushToast(
          "error",
          "Addresses load failed",
          getErrMsg(direccionesRes.reason, "We couldn’t load your saved addresses.")
        );
      }

      if (pedidosRes.status === "fulfilled") {
        const pedidos = pedidosRes.value;
        setOrders(Array.isArray(pedidos) ? pedidos : []);
      } else {
        console.error("Orders error:", pedidosRes.reason?.response?.data || pedidosRes.reason);
        setOrders([]);
        pushToast(
          "error",
          "Orders load failed",
          getErrMsg(pedidosRes.reason, "We couldn’t load your orders.")
        );
      }

      setLoadingPerfil(false);
      setLoadingOrders(false);
    };

    loadAll();
  }, []);

  useEffect(() => {
    setEditingAddress(null);
    addressBaselineRef.current = null;
  }, [tab]);

  const fullName =
    profile.nombre || profile.apellido
      ? `${profile.nombre} ${profile.apellido}`.trim()
      : "Customer";

  /* En el saludo va el nombre comercial, que es como el cliente llama a su
     propia empresa. Si todavia no lo cargo se usa la razon social, que si
     se pidio en el alta. */
  const nombreEmpresa =
    profile.tipo_cliente === "comercial"
      ? norm(profile.empresa) || norm(profile.razon_social)
      : "";

  return (
  <div className="adc-account-scope">
    <div className="adc-account">
      <ToastStack toasts={toasts} onClose={closeToast} />

      <div className="adc-account-header">
        <div className="adc-header-title">
          <h1 className="adc-title">My account</h1>
           <p className="adc-subtitle">
            Welcome back, <strong>{fullName}</strong>
            {nombreEmpresa ? (
              <>
                {" · "}
                <strong>{nombreEmpresa}</strong>
              </>
            ) : null}
          </p>
        </div>


         <div className="adc-header-actions">
        <button
          className="adc-btn-backTostore"
          onClick={() => {
            window.location.href = "/store";
          }}
          >
          Back to store
        </button>

        <button
          className="adc-btn-logout"
          onClick={() => {
            logoutCliente();
            window.location.href = "/store/login";
          }}
        >
          Logout
        </button>

        </div>
      </div>
      <div className="adc-layout">
        <nav className="adc-tabs">
          <button
            className={tab === "orders" ? "active" : ""}
            onClick={() => setTab("orders")}
          >
            Orders
          </button>
          <button
            className={tab === "profile" ? "active" : ""}
            onClick={() => setTab("profile")}
          >
            Profile
          </button>
        </nav>

        <section className="adc-tab-content">
          {tab === "orders" && (
            <div className="adc-orders">
              {loadingOrders && <p className="muted">Loading orders…</p>}
              {!loadingOrders && orders.length === 0 && (
                <p className="muted">You don’t have any orders yet.</p>
              )}

              {!loadingOrders && orders.length > 0 && (
                <div className="adc-orders-panel">
                  <div className="adc-orders-head">
                    <div>
                      <h3 className="adc-orders-title">Your orders</h3>
                      <p className="muted">
                        Total: <strong>{orders.length}</strong>
                      </p>
                    </div>
                  </div>

                  <div className="adc-orders-scroll">
                    {orders.map((o) => {
                      /* El mismo texto que adentro del detalle. Antes el
                         listado mostraba "Pendiente" y el modal del mismo
                         pedido decia "Processing". */
                      const estadoTexto =
                        traducirEstadoPedido(o.estado_texto) || "—";
                      const paid = !!o.pagado;

                      const estadoClass =
                        o.estado === 0
                          ? "is-pending"
                          : o.estado === 1
                          ? "is-process"
                          : o.estado === 2
                          ? "is-shipped"
                          : o.estado === 3
                          ? "is-delivered"
                          : o.estado === 4
                          ? "is-cancelled"
                          : "";

                      /* Que trae el pedido. Con "Order #51" a secas el
                         cliente tiene que abrir cada uno para saber cual
                         es cual. */
                      const piezas = Number(o.total_piezas || 0);
                      const lineas = Number(o.total_lineas || 0);
                      const resumen = [
                        piezas
                          ? `${piezas} ${piezas === 1 ? "item" : "items"}`
                          : null,
                        o.primer_producto
                          ? lineas > 1
                            ? `${o.primer_producto} +${lineas - 1} more`
                            : o.primer_producto
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ");

                      /* A donde va. */
                      const destino = [o.ciudad, o.pais]
                        .map((x) => String(x ?? "").trim())
                        .filter(Boolean)
                        .join(", ");

                      const docs = Number(o.total_documentos || 0);

                      /* El recorrido, con la misma regla que el detalle. */
                      const entregadoEl = o.fecha_entregado
                        ? formatDateTime(o.fecha_entregado)
                        : null;
                      const enviadoEl = o.fecha_enviado
                        ? formatDateTime(o.fecha_enviado)
                        : null;

                      return (
                        <div key={o.pedido_id} className="adc-order-item">
                          <div className="adc-order-main">
                            <div className="adc-order-top">
                              <div className="adc-order-id">
                                Order <span>#{o.pedido_id}</span>
                                {o.no_po ? (
                                  <span className="adc-order-po">
                                    PO #{o.no_po}
                                  </span>
                                ) : null}
                              </div>

                              <div className="adc-order-date muted">
                                {o.fecha_creado
                                  ? formatDateTime(o.fecha_creado)
                                  : "—"}
                              </div>
                            </div>

                            <div className="adc-order-meta">
                              <span className={`adc-badge ${estadoClass}`}>
                                {estadoTexto}
                              </span>

                              <span
                                className={`adc-badge ${
                                  paid ? "is-paid" : "is-unpaid"
                                }`}
                              >
                                {o.pago_texto || (paid ? "Paid" : "Unpaid")}
                              </span>

                              {entregadoEl ? (
                                <span className="adc-order-hito">
                                  Delivered {entregadoEl}
                                </span>
                              ) : enviadoEl ? (
                                <span className="adc-order-hito">
                                  Shipped {enviadoEl}
                                </span>
                              ) : null}
                            </div>

                            {resumen ? (
                              <div className="adc-order-resumen">{resumen}</div>
                            ) : null}

                            <div className="adc-order-bottom">
                              <div className="adc-order-total">
                                Total:{" "}
                                <strong>{formatMoney(o.total_factura)}</strong>
                              </div>

                              {o.nombre_metodo_envio && (
                                <div className="adc-order-ship muted">
                                  {o.nombre_metodo_envio}
                                </div>
                              )}

                              {destino && (
                                <div className="adc-order-ship muted">
                                  To {destino}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="adc-order-acciones">
                            <button
                              className="adc-btn-secondary"
                              onClick={() => setSelectedOrder(o.pedido_id)}
                            >
                              View details
                            </button>

                            {/* Con la cantidad, y apagado si no hay
                                ninguno: el boton se veia igual en un pedido
                                con la factura lista y en uno sin nada, y el
                                cliente lo apretaba para encontrar un modal
                                vacio. */}
                            <button
                              className="btn-state btn-docs"
                              title={
                                docs
                                  ? "Invoices and files for this order"
                                  : "No documents attached yet"
                              }
                              disabled={docs === 0}
                              onClick={() => {
                                setDocsOrderId(o.pedido_id);
                                setShowDocs(true);
                              }}
                            >
                              {docs ? `Documents (${docs})` : "No documents"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "profile" && (
            <div className="adc-card adc-profile-form">
              {loadingPerfil && <p className="muted">Loading profile…</p>}

              {!loadingPerfil && (
                <>
                  <header className="adc-form-head">
                    <h3>Profile information</h3>
                    <p className="muted">
                      Manage your contact and company information.
                    </p>
                  </header>

                  {/* Los datos y las direcciones son la misma ficha del
                      cliente, asi que van en una sola pantalla. El
                      formulario se queda con la columna ancha porque tiene
                      diez campos; las direcciones son una lista corta y se
                      leen bien en una columna angosta al costado. */}
                  <div className="adc-perfil-split">
                    <div className="adc-perfil-col">

                  {/* Tres secciones con titulo en vez de una lista corrida
                      de campos: quien entra a cambiar su contrasena no tiene
                      que leer los diez inputs para encontrar los dos que
                      busca. */}
                  <section className="adc-form-section">
                    <h4 className="adc-section-title">Contact information</h4>
                    <p className="muted">
                      Information for the primary contact of this account.
                    </p>

                    <div className="adc-form-grid">
                      <div className="adc-field">
                        <label>First name</label>
                        <input
                          name="nombre"
                          minLength={2}
                          maxLength={100}
                          value={profile.nombre}
                          onChange={handleProfileChange}
                        />
                      </div>

                      <div className="adc-field">
                        <label>Last name</label>
                        <input
                          name="apellido"
                          minLength={2}
                          maxLength={100}
                          value={profile.apellido}
                          onChange={handleProfileChange}
                        />
                      </div>

                      <div className="adc-field">
                        <label>Email address</label>
                        <input
                          name="correo"
                          type="email"
                          maxLength={120}
                          value={profile.correo}
                          onChange={handleProfileChange}
                        />
                      </div>

                      {/* El telefono paso a ser obligatorio: es el unico
                          modo de avisar si hay un problema con un pedido
                          en curso. */}
                      <div className="adc-field">
                        <label>Phone number</label>

                        <div className="adc-phoneRow">
                          <input
                            className="adc-input adc-input--code"
                            name="codigo_telefono"
                            inputMode="tel"
                            placeholder="+1"
                            maxLength={7}
                            aria-label="Country code"
                            value={profile.codigo_telefono}
                            onChange={handleProfileChange}
                          />

                          <input
                            className="adc-input"
                            name="telefono"
                            inputMode="numeric"
                            placeholder={
                              phoneMeta
                                ? phoneMeta.minLength === phoneMeta.maxLength
                                  ? `${phoneMeta.maxLength} digits`
                                  : `${phoneMeta.minLength}-${phoneMeta.maxLength} digits`
                                : "Numbers only"
                            }
                            value={profile.telefono}
                            onChange={handleProfileChange}
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Solo para cuentas comerciales. En una individual estos
                      campos no existen en la base, asi que mostrarlos vacios
                      seria ofrecer algo que no se guarda. */}
                  {esCuentaComercial && (
                    <section className="adc-form-section">
                      <h4 className="adc-section-title">Company details</h4>
                      <p className="muted">
                        Used on your invoices and purchase orders.
                      </p>

                      <div className="adc-form-grid">
                        {/* El obligatorio es el legal: es el que va en la
                            factura. El comercial quedo opcional porque
                            muchos talleres facturan y se conocen con el
                            mismo nombre y no tienen un segundo. */}
                        <div className="adc-field">
                          <label>Legal company name</label>
                          <input
                            name="razon_social"
                            maxLength={200}
                            value={profile.razon_social}
                            onChange={handleProfileChange}
                          />
                          <div className="adc-field-help">
                            Registered company name that will appear on
                            invoices.
                          </div>
                        </div>

                        <div className="adc-field">
                          <label>Trade name</label>
                          <input
                            name="empresa"
                            maxLength={150}
                            value={profile.empresa}
                            onChange={handleProfileChange}
                          />
                          <div className="adc-field-help">
                            How your business is known day to day.
                          </div>
                        </div>

                        <div className="adc-field">
                          <label>VAT/Tax ID</label>
                          <input
                            name="vat_tax_id"
                            maxLength={50}
                            value={profile.vat_tax_id}
                            onChange={handleProfileChange}
                          />
                          <div className="adc-field-help">
                            NIT, VAT, EIN or the equivalent in your country.
                          </div>
                        </div>

                        {/* En un taller la factura va a contabilidad y la
                            cuenta la usa el encargado de compras: no tiene
                            por que ser el mismo correo. */}
                        <div className="adc-field">
                          <label>Billing email</label>
                          <input
                            name="correo_facturacion"
                            type="email"
                            maxLength={120}
                            value={profile.correo_facturacion}
                            onChange={handleProfileChange}
                          />
                          <div className="adc-field-help">
                            Where invoices are sent, if it is not the account
                            email.
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  <section className="adc-form-section">
                    <h4 className="adc-section-title">Account access</h4>
                    <p className="muted">
                      Your username and password. The current password is
                      required to set a new one; leave both empty to keep the
                      one you have.
                    </p>

                    <div className="adc-form-grid">
                      {/* El usuario no se cambia: es la llave con la que se
                          entra y con la que quedan firmadas las ordenes. */}
                      <div className="adc-field">
                        <label>Username</label>
                        <input name="usuario" value={profile.usuario} readOnly />
                      </div>

                      <div className="adc-field" aria-hidden="true" />

                      <div className="adc-field">
                        <label>Current password</label>
                        <input
                          type="password"
                          name="currentPassword"
                          autoComplete="current-password"
                          value={profile.currentPassword}
                          onChange={handleProfileChange}
                        />
                        {faltaPasswordActual ? (
                          <p className="adc-field-error" role="alert">
                            Enter your current password to change it.
                          </p>
                        ) : null}
                      </div>

                      <div className="adc-field">
                        <label>New password</label>
                        <input
                          type="password"
                          name="newPassword"
                          autoComplete="new-password"
                          minLength={8}
                          maxLength={72}
                          value={profile.newPassword}
                          onChange={handleProfileChange}
                        />
                        {faltaPasswordNueva ? (
                          <p className="adc-field-error" role="alert">
                            Enter the new password.
                          </p>
                        ) : (
                          <div className="adc-field-help">
                            At least 8 characters.
                          </div>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* El boton va en una barra abajo y no suelto al final:
                      asi se ve que cierra el formulario entero y no solo la
                      ultima seccion. El motivo de que este apagado se dice
                      con texto, que antes solo estaba en el title y habia
                      que dejar el mouse encima para verlo. */}
                  <div className="adc-form-footer">
                    <span className="adc-form-footer-nota muted">
                      {isProfileDirty ? "" : "No changes to save"}
                    </span>

                    <button
                      className="adc-btn-primary"
                      onClick={saveProfile}
                      disabled={savingProfile || !isProfileDirty}
                    >
                      {savingProfile ? "Saving…" : "Save changes"}
                    </button>
                  </div>

                    </div>

                <aside className="adc-direcciones">
                      <div className="adc-address-header">
                        <div>
                          <h3>Shipping addresses</h3>
                          <p className="muted">Manage your delivery locations.</p>
                        </div>

                        {editingAddress ? null : (
                          <button className="adc-btn-primary" onClick={addNewAddress}>
                            Add address
                          </button>
                        )}
                      </div>

                      {addresses.length === 0 && !editingAddress && (
                        <p className="muted">No addresses saved yet.</p>
                      )}

                      {/* Con el formulario abierto la lista se esconde: en
                          una columna angosta, el editor mas las tarjetas
                          hacen una tira larguisima, y mientras se escribe
                          una direccion las otras no se estan mirando. */}
                      <div
                        className="adc-address-grid"
                        hidden={!!editingAddress}
                      >
                        {addresses.map((a) => (
                          <div key={a.cliente_direccion_id} className="adc-address-card">
                            <div>
                              <div className="adc-dir-titulo">
                                <strong>{a.nombre_direccion}</strong>
                                {/* Ternario y no `&&`: la API manda 0 o 1, y
                                    `0 && ...` devuelve 0, que React imprime
                                    como un "0" pegado al nombre. */}
                                {a.direccion_principal ? (
                                  <span className="adc-dir-tag">Shipping</span>
                                ) : null}
                                {a.direccion_facturacion ? (
                                  <span className="adc-dir-tag">Billing</span>
                                ) : null}
                              </div>

                              {a.nombre_destinatario ? (
                                <p className="adc-dir-linea adc-dir-empresa">
                                  {a.nombre_destinatario}
                                </p>
                              ) : null}


                              {a.empresa ? (
                                <p className="adc-dir-linea">{a.empresa}</p>
                              ) : null}

                              <p className="adc-dir-linea">
                                {unir([a.direccion_linea_1, a.direccion_linea_2])}
                              </p>

                              <p className="adc-dir-linea">
                                {unir([
                                  a.ciudad,
                                  unir([a.estado_provincia, a.codigo_postal], " "),
                                ])}
                              </p>

                              <p className="adc-dir-linea">{a.pais}</p>

                              {/* El telefono se guarda y hasta ahora solo se
                                  veia entrando a editar. */}
                              {a.telefono ? (
                                <p className="adc-dir-linea adc-dir-tel">
                                  {unir([a.codigo_telefono, a.telefono], " ")}
                                </p>
                              ) : null}
                            </div>

                            <div className="adc-address-actions">
                              <button className="adc-btn-edit" onClick={() => editAddress(a)}>
                                Edit
                              </button>
                              <button
                                className="adc-btn-delete"
                                onClick={() => deleteAddress(a.cliente_direccion_id)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {editingAddress && (
                        <div className="adc-address-form">
                          <h4>
                            {editingAddress.cliente_direccion_id ? "Edit address" : "New address"}
                          </h4>

                          {/* El orden sigue el sobre: como se llama la
                              direccion, a nombre de quien se recibe, donde
                              queda, y al final a quien llamar al llegar. El
                              telefono no es parte de la direccion, asi que
                              no se mete entre las lineas de la calle. */}
                          {/* El orden sigue el sobre: como se llama la
                              direccion, quien la recibe, y recien despues
                              donde queda. El pais va antes que la calle
                              porque de el dependen como se llaman y que
                              forma tienen el estado y el codigo postal. */}
                          <div className="adc-form-grid">
                            <div className="adc-field">
                              <label>Address name</label>
                              <input
                                name="nombre_direccion"
                                placeholder="Main office, Miami warehouse"
                                maxLength={150}
                                value={editingAddress.nombre_direccion}
                                onChange={handleAddressChange}
                              />
                              <div className="adc-field-help">
                                A name to help you identify this address.
                              </div>
                            </div>

                            {/* Quien firma cuando llega el paquete. Es lo
                                que va impreso en la etiqueta de envio, y
                                no tiene por que ser el titular de la
                                cuenta: en una bodega recibe el encargado. */}
                            <div className="adc-field">
                              <label>Recipient&apos;s full name</label>
                              <input
                                name="nombre_destinatario"
                                autoComplete="name"
                                placeholder="John Smith"
                                maxLength={150}
                                value={editingAddress.nombre_destinatario}
                                onChange={handleAddressChange}
                              />
                            </div>


                            {esCuentaComercial ? (
                              <div className="adc-field">
                                <label>Company name (optional)</label>
                                <input
                                  name="empresa"
                                  autoComplete="organization"
                                  placeholder="Classrom, Inc."
                                  maxLength={150}
                                  value={editingAddress.empresa}
                                  onChange={handleAddressChange}
                                />
                              </div>
                            ) : null}

                            <div className="adc-field">
                              <label>Phone number</label>

                              <div className="adc-phoneRow">
                                <input
                                  className="adc-input adc-input--code"
                                  name="codigo_telefono"
                                  inputMode="tel"
                                  placeholder="+1"
                                  maxLength={7}
                                  aria-label="Country code"
                                  value={editingAddress.codigo_telefono}
                                  onChange={handleAddressChange}
                                />

                                <input
                                  className="adc-input"
                                  name="telefono"
                                  type="tel"
                                  inputMode="tel"
                                  autoComplete="tel"
                                  maxLength={50}
                                  value={editingAddress.telefono}
                                  onChange={handleAddressChange}
                                />
                              </div>

                              <div className="adc-field-help">
                                Used only if there is a problem with the
                                delivery.
                              </div>
                            </div>

                            <div className="adc-field">
                              <label>Country</label>
                              <input
                                name="pais"
                                autoComplete="country-name"
                                placeholder="United States"
                                maxLength={100}
                                value={editingAddress.pais}
                                onChange={handleAddressChange}
                              />
                            </div>

                            <div className="adc-field">
                              <label>Address line 1</label>
                              <input
                                name="direccion_linea_1"
                                autoComplete="address-line1"
                                placeholder="Street address and building number"
                                value={editingAddress.direccion_linea_1}
                                onChange={handleAddressChange}
                              />
                            </div>

                            <div className="adc-field">
                              <label>Address line 2 (optional)</label>
                              <input
                                name="direccion_linea_2"
                                autoComplete="address-line2"
                                placeholder="Suite, unit, floor, hangar, building"
                                value={editingAddress.direccion_linea_2}
                                onChange={handleAddressChange}
                              />
                            </div>

                            <div className="adc-field">
                              <label>City</label>
                              <input
                                name="ciudad"
                                autoComplete="address-level2"
                                value={editingAddress.ciudad}
                                onChange={handleAddressChange}
                              />
                            </div>

                            <div className="adc-field">
                              <label>State, province or region</label>
                              <input
                                name="estado_provincia"
                                autoComplete="address-level1"
                                value={editingAddress.estado_provincia}
                                onChange={handleAddressChange}
                              />
                            </div>

                            <div className="adc-field">
                              <label>Postal code / ZIP code</label>
                              <input
                                name="codigo_postal"
                                autoComplete="postal-code"
                                value={editingAddress.codigo_postal}
                                onChange={handleAddressChange}
                              />
                            </div>
                          </div>

                          {/* Son dos cosas distintas: la mercaderia puede
                              ir a la bodega y la factura a la oficina. */}
                          <label className="adc-checkbox">
                            <input
                              type="checkbox"
                              name="direccion_principal"
                              checked={editingAddress.direccion_principal}
                              onChange={handleAddressChange}
                            />
                            Set as default shipping address
                          </label>

                          <label className="adc-checkbox">
                            <input
                              type="checkbox"
                              name="direccion_facturacion"
                              checked={editingAddress.direccion_facturacion}
                              onChange={handleAddressChange}
                            />
                            Set as default billing address
                          </label>

                          <div className="adc-form-actions">
                            <button
                              className="adc-btn-primary"
                              onClick={saveAddress}
                              disabled={savingAddress || !isAddressDirty || !isAddressValid}
                              title={
                                !isAddressDirty
                                  ? "No changes to save"
                                  : !isAddressValid
                                  ? "Fill required fields"
                                  : ""
                              }
                            >
                              {savingAddress ? "Saving…" : "Save address"}
                            </button>

                            <button
                              className="adc-btn-secondary"
                              onClick={() => {
                                setEditingAddress(null);
                                addressBaselineRef.current = null;
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                </aside>
                  </div>
                </>
              )}
            </div>
          )}
        </section>
      </div>

      {selectedOrder && (
        <OrdersModal pedidoId={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}

    </div>
      {showDocs && docsOrderId && (
        <OrderDocsModal
          pedidoId={docsOrderId}
          onClose={() => {
            setShowDocs(false);
            setDocsOrderId(null);
          }}
        />
      )}
      </div>
  );
}
